import type { useDND } from "../../contexts/DNDContext";
import type { NotificationType } from "../../contexts/ToastContext";
import { isElectron } from "../../lib/platform";
import { shouldDeliverRiskFlowHeadlineCard } from "../../lib/riskflow-notification-policy";
import type {
  NotificationCategory,
  NotificationPrefs,
} from "../../lib/user-preferences";
import type { Notification } from "../NotificationToast";

export const RISKFLOW_CATEGORIES = new Set([
  "riskflow",
  "geopolitical_alerts",
  "econ_alerts",
]);
export const RISK_SIGNAL_CATEGORIES = new Set(["risk_signals", "riskSignal"]);

export function shouldRenderRiskFlowCard(
  item: Pick<Notification, "category" | "ivScore">,
  isTradingSurfaceActive: boolean,
): boolean {
  return (
    !RISKFLOW_CATEGORIES.has(item.category) ||
    shouldDeliverRiskFlowHeadlineCard(item, isTradingSurfaceActive)
  );
}

export function appendUnique(
  current: Notification[],
  incoming: Notification[],
): Notification[] {
  const keys = new Set(incoming.map(notificationKey));
  return [
    ...incoming,
    ...current.filter((item) => !keys.has(notificationKey(item))),
  ].slice(0, 5);
}

export function claimNotification(
  item: Notification,
  claimedKeys: Set<string>,
): boolean {
  const key = notificationKey(item);
  if (claimedKeys.has(key)) return false;
  claimedKeys.add(key);
  if (claimedKeys.size > 5_000) {
    const oldest = claimedKeys.values().next().value;
    if (oldest) claimedKeys.delete(oldest);
  }
  return true;
}

export function queueDuringDnd(
  item: Notification,
  tryQueue: ReturnType<typeof useDND>["tryQueue"],
): boolean {
  return tryQueue(
    {
      id: item.id,
      type: item.type,
      title: item.title,
      message: item.message,
      timestamp: item.timestamp,
      severity: queueSeverity(item.severity),
    },
    item.severity === "critical" || item.severity === "error"
      ? "system-error"
      : "non-critical",
  );
}

export function canDeliver(
  item: Notification,
  prefs: NotificationPrefs,
): boolean {
  const channelEnabled = isElectron()
    ? prefs.deliveryChannels.desktop
    : prefs.deliveryChannels.web;
  if (!channelEnabled) return false;
  if (item.severity === "critical") return true;
  if (prefs.blockedCategories.includes(item.category as NotificationCategory))
    return false;
  if (prefs.econOnlyMode && item.category !== "econ_alerts") return false;
  if (prefs.criticalOnly) return false;
  return severityRank(item.severity) >= severityRank(prefs.severityThreshold);
}

export function notificationType(item: Notification): NotificationType {
  if (RISKFLOW_CATEGORIES.has(item.category)) return "riskflow-watch";
  if (RISK_SIGNAL_CATEGORIES.has(item.category)) return "risk-signal";
  if (item.type === "iv") return "iv-alert";
  if (item.type === "tilt") return "tilt-alert";
  if (item.type === "trade") return "trade-alert";
  if (item.type === "warning") return "general";
  return "news-alert";
}

export function notificationFromDetail(
  detail: Partial<Notification>,
): Notification {
  const category = String(detail.category ?? "riskflow");
  return {
    id: detail.id ?? `riskflow-card-${Date.now()}`,
    type: typeForCategory(category),
    category,
    severity: detail.severity ?? "medium",
    title: detail.title ?? "Fintheon · RiskFlow",
    message: detail.message ?? "",
    timestamp: detail.timestamp instanceof Date ? detail.timestamp : new Date(),
    url: detail.url,
    eventId: detail.eventId,
    ivScore: detail.ivScore,
    riskType: detail.riskType,
    source: detail.source,
    imageUrl: detail.imageUrl,
    videoUrl: detail.videoUrl,
    metadata: detail.metadata,
  };
}

export function notificationFromBackend(
  row: Record<string, unknown>,
): Notification {
  const category = String(row.category ?? row.type ?? "system");
  return notificationFromDetail({
    id: String(row.id),
    type: typeForCategory(category),
    category,
    severity: row.severity as Notification["severity"],
    title: String(row.title ?? "Fintheon"),
    message: String(row.body ?? row.message ?? ""),
    timestamp: new Date(String(row.createdAt ?? Date.now())),
    url: typeof row.url === "string" ? row.url : undefined,
    eventId: String(row.eventId ?? row.itemId ?? "") || undefined,
    ivScore:
      typeof row.ivScore === "number"
        ? row.ivScore
        : typeof row.score === "number"
          ? row.score
          : null,
    riskType: row.riskType as Notification["riskType"],
    source: row.source as Notification["source"],
    imageUrl: typeof row.imageUrl === "string" ? row.imageUrl : null,
    videoUrl: typeof row.videoUrl === "string" ? row.videoUrl : null,
    metadata:
      row.metadata && typeof row.metadata === "object"
        ? (row.metadata as Record<string, unknown>)
        : undefined,
  });
}

function notificationKey(item: Notification): string {
  return (item.eventId || item.id).replace(/^backend-/, "");
}

function queueSeverity(severity: Notification["severity"]) {
  if (severity === "error" || severity === "critical") return "error";
  if (severity === "warning" || severity === "high") return "warning";
  if (severity === "success") return "success";
  return "info";
}

function severityRank(severity?: Notification["severity"]): number {
  if (severity === "critical" || severity === "error") return 4;
  if (severity === "high" || severity === "warning") return 3;
  if (severity === "medium") return 2;
  return 1;
}

function typeForCategory(category: string): Notification["type"] {
  if (RISK_SIGNAL_CATEGORIES.has(category)) return "riskSignal";
  if (category === "riskflow" || category === "dailyBrief") return "iv";
  if (category === "toolApprovals" || category === "maintenance_request")
    return "trade";
  if (category === "system" || category === "test") return "warning";
  return "news";
}
